import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import searchIndex from "../../utils/searchIndex";
import { IoSearch } from "react-icons/io5";
import { arClass } from "../../utils/useArClass";


const searchTexts = {
  en: {
    placeholder: "Search...",
    results: "Results",
    noResults: "No results",
  },
  fr: {
    placeholder: "Rechercher...",
    results: "Résultats",
    noResults: "Aucun résultat",
  },
  ar: {
    placeholder: "بحث",
    results: "النتائج",
    noResults: "لا توجد نتائج",
  },
};


export default function SearchBar({ className = "" ,darkMode}) {
  const defaultLanguage = useSelector((s) => s.settings.defaultLanguage);
  const placeholder = defaultLanguage === "fr" ? "Rechercher..." : "Search...";
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const q = query.toLowerCase().trim();

      // Local index search (fast) — score on label / keywords / description
      const local = searchIndex[defaultLanguage]
        .map((item) => {
          const label = (item.label || "").toLowerCase();
          const desc = (item.description || "").toLowerCase();
          const keywords = (item.keywords || []).map((k) => k.toLowerCase());

          let score = 0;
          if (label.includes(q)) score += 4;
          if (desc.includes(q)) score += 1;

          // keywords: partial match or exact
          for (const kw of keywords) {
            if (kw === q) {
              score += 3;
              break;
            }
            if (kw.includes(q) || q.includes(kw)) {
              score += 2;
            }
          }

          return { ...item, score };
        })
        .filter((it) => it.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);

      // Try backend search if available (optional, graceful fallback)
      let apiResults = [];
      try {
        setLoadingApi(true);
        const res = await api.get(`/search/?q=${encodeURIComponent(q)}`);
        // Expecting res.data.results as an array of { label, path, type, description }
        if (res?.data?.results && Array.isArray(res.data.results)) {
          apiResults = res.data.results.map((r, i) => ({ ...r, id: `api-${i}` }));
        }
      } catch (err) {
        // ignore backend errors — local results still shown
      } finally {
        setLoadingApi(false);
      }

      const merged = [...local, ...apiResults]
        .map((r, i) => ({ ...r, _idx: i }))
        .filter((v, i, a) => a.findIndex((x) => x.path === v.path) === i)
        .slice(0, 10);

      setResults(merged);
      setOpen(true);
    }, 220);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const onSelect = (item) => {
    setOpen(false);
    setQuery("");
    if (!item) return;
    if (item.path && item.path.startsWith("/")) {
      nav(item.path);
    } else if (item.type === "api" && item.path) {
      // navigate to a table view of endpoint if such page exists
      nav(item.path);
    } else if (item.url) {
      window.open(item.url, "_blank");
    }
  };

  // theme-aware classes for dropdown
  const dropdownBase = darkMode
    ? "bg-gray-900 border border-gray-700 text-gray-100"
    : "bg-white border border-gray-200 text-gray-900";
  const headerTextClass = darkMode ? "text-gray-300" : "text-gray-500";
  const emptyTextClass = darkMode ? "text-gray-400" : "text-gray-500";
  const itemHoverClass = darkMode ? "hover:bg-gray-800" : "hover:bg-slate-100";
  const itemTitleClass = darkMode ? "text-sm font-medium text-gray-100" : "text-sm font-medium text-gray-900";
  const itemMetaClass = darkMode ? "text-[12px] text-gray-400" : "text-[12px] text-gray-400";
  const itemDescClass = darkMode ? "text-xs text-gray-300" : "text-xs text-gray-500";

  return (
    <div ref={ref} className={arClass(` relative ${className} w-[26rem] max-sm:w-56 max-lg:w-[20rem] max-sm:hidden`)}>
      <div className={arClass("flex items-center gap-2 w-full")}>
        <div className={arClass("relative w-full")}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query && setOpen(true)}
            placeholder={searchTexts[defaultLanguage]?.placeholder || "Search..."}
            className={` rounded-full border main-border-color text-sm w-full ${
              darkMode ? "placeholder-gray-400 !text-gray-200 bg-gray-900 !border-gray-300 focus:border-blue-500 focus:bg-gray-800"
              : "placeholder-gray-500 !text-gray-800 bg-gray-200 !border-gray-300 focus:border-blue-600 focus:bg-white"
            }
            ${
              defaultLanguage === "ar" ? "text-right pr-10 pl-3 p-2" : "text-left pl-10 pr-3 p-2"
            }
            `}
            aria-label="global-search"
          />
          <span className={`absolute  text-xl ${
            darkMode ? "text-gray-400" : "text-gray-500"
          } ${
            defaultLanguage === "ar" ? "right-3 top-1/2 -translate-y-1/2" : "left-3 top-1/2 -translate-y-1/2"
          }
          `}>
            <IoSearch />
          </span>
        </div>
      </div>

      {open && (
        <div className={arClass(`absolute z-40 mt-2 w-[26rem] max-sm:w-56 max-lg:w-[20rem] rounded-lg shadow-lg overflow-hidden ${dropdownBase}`)}>
          <div className={arClass(`p-2 border-b ${headerTextClass} font-semibold flex items-center gap-2`)}>
            <span>{defaultLanguage === "fr" ? "Résultats" : defaultLanguage === "ar" ? "النتائج" : "Results"}</span>
            {loadingApi && <span className={arClass("text-[11px]")}>…</span>}
          </div>
          <div className={arClass("max-h-64 overflow-y-auto")}>
            {results.length === 0 ? (
              <div className={arClass(`p-3 text-sm ${emptyTextClass}`)}>{defaultLanguage === "fr" ? "Aucun résultat" :defaultLanguage === "ar" ? "لا توجد نتائج" : "No results"}</div>
            ) : (
              results.map((r) => (
                <button
                  key={r.id || r._idx}
                  onClick={() => onSelect(r)}
                  className={arClass(`w-full text-left p-3 ${itemHoverClass} flex flex-col gap-1 transition-colors`)}
                >
                  <div className={arClass("flex items-center justify-between")}>
                    <div className={itemTitleClass}>{r.label}</div>
                    <div className={itemMetaClass}>{r.type?.toUpperCase() || ""}</div>
                  </div>
                  {r.description && <div className={itemDescClass}>{r.description}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}